import { Printer } from 'lucide-react';
import { formatPaise, formatDate } from '../../lib/format.js';
import { printHtml } from '../lib/print.js';
import { escapeHtml } from '../lib/html-escape.js';
import { formatAddressLines } from '../lib/address-snapshot.js';
import type { InvoicePreview } from '../types/orders.js';

export interface InvoiceViewProps {
  invoice: InvoicePreview;
}

/** "Order Invoice Preview" -- labeled a preview, never a legally-numbered invoice, since the schema has no populated invoice-number sequence (see docs/admin-invoice-packing-slip.md "Invoice numbering"). */
export function InvoiceView({ invoice }: InvoiceViewProps) {
  function handlePrint() {
    const addressLines = formatAddressLines(invoice.billingAddress).map(escapeHtml).join('<br/>');
    const itemRows = invoice.items
      .map(
        (item) => `<tr>
          <td>${escapeHtml(item.productName)} — ${escapeHtml(item.variantName)}<br/><span class="muted">SKU ${escapeHtml(item.sku)}</span></td>
          <td>${item.quantity}</td>
          <td>${escapeHtml(formatPaise(item.unitPrice.amountPaise))}</td>
          <td>${escapeHtml(formatPaise(item.lineDiscount.amountPaise))}</td>
          <td>${escapeHtml(formatPaise(item.lineTax.amountPaise))}</td>
          <td>${escapeHtml(formatPaise(item.lineTotal.amountPaise))}</td>
        </tr>`,
      )
      .join('');

    printHtml(
      `Order Invoice Preview - ${invoice.orderNumber}`,
      `<h1>Order Invoice Preview</h1>
      <p class="muted">Order ${escapeHtml(invoice.orderNumber)} · ${escapeHtml(formatDate(invoice.orderDate))}</p>
      <h2>Billing / delivery address</h2>
      <p>${addressLines}</p>
      <table>
        <thead><tr><th>Item</th><th>Qty</th><th>Unit price</th><th>Discount</th><th>Tax</th><th>Line total</th></tr></thead>
        <tbody>${itemRows}</tbody>
      </table>
      <table class="totals">
        <tr><td class="label">Subtotal</td><td class="value">${escapeHtml(formatPaise(invoice.subtotal.amountPaise))}</td></tr>
        <tr><td class="label">Discount</td><td class="value">-${escapeHtml(formatPaise(invoice.discount.amountPaise))}</td></tr>
        <tr><td class="label">Tax</td><td class="value">${escapeHtml(formatPaise(invoice.tax.amountPaise))}</td></tr>
        <tr><td class="label">Delivery fee</td><td class="value">${escapeHtml(formatPaise(invoice.deliveryFee.amountPaise))}</td></tr>
        <tr><td class="label"><strong>Grand total</strong></td><td class="value"><strong>${escapeHtml(formatPaise(invoice.total.amountPaise))}</strong></td></tr>
      </table>
      <p class="muted">Payment method: ${escapeHtml(invoice.paymentMethod)} · Status: ${escapeHtml(invoice.paymentStatus ?? 'unknown')}</p>`,
    );
  }

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <p className="text-sm text-gray-500">Order Invoice Preview — not a legally numbered invoice.</p>
        <button
          type="button"
          onClick={handlePrint}
          className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 px-3 py-1.5 text-sm hover:bg-gray-50"
        >
          <Printer className="h-4 w-4" aria-hidden="true" />
          Print
        </button>
      </div>
      <table className="w-full text-left text-sm">
        <thead>
          <tr className="border-b border-gray-100 text-xs uppercase tracking-wide text-gray-400">
            <th className="py-2 pr-4 font-medium">Item</th>
            <th className="py-2 pr-4 font-medium">Qty</th>
            <th className="py-2 pr-4 font-medium">Unit price</th>
            <th className="py-2 pr-4 font-medium">Line total</th>
          </tr>
        </thead>
        <tbody>
          {invoice.items.map((item, index) => (
            <tr key={index} className="border-b border-gray-50">
              <td className="py-2 pr-4">
                {item.productName} — {item.variantName}
              </td>
              <td className="py-2 pr-4">{item.quantity}</td>
              <td className="py-2 pr-4">{formatPaise(item.unitPrice.amountPaise)}</td>
              <td className="py-2 pr-4">{formatPaise(item.lineTotal.amountPaise)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="mt-3 flex justify-end">
        <p className="text-sm font-semibold text-gray-900">
          Grand total: {formatPaise(invoice.total.amountPaise)}
        </p>
      </div>
    </div>
  );
}
